'use client'

import { useTranslations } from 'next-intl'
import { useEffect } from 'react'

import { TVerificationData } from '@/features/sign-up'
import { Dialog } from '@/shared/ui'

import { EmailContent } from './email-content'
import { usePassportOCR } from '../../api/passport-ocr'
import { useValidatePhoneNumber } from '../../api/validate-phone-number'
import { TPassportVerificationForm } from '../../model/passport-verification-form'
import { PassportContent } from '../passport-content'

export const PASSPORT_EMAIL_VERIFICATION_DIALOG = 'PASSPORT_EMAIL_VERIFICATION'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSuccessPassport: (verification: TVerificationData) => void
  onSuccess: (emailCertToken: string) => void
  onMaxFail: (verification: TVerificationData) => void
  defaultValues?: Partial<TPassportVerificationForm>
  email: string
  step: 'passport' | 'email'
}

export const PassportEmailDialog = ({
  isOpen,
  onClose,
  onSuccessPassport,
  onSuccess,
  onMaxFail,
  defaultValues,
  email,
  step,
}: Props) => {
  const t = useTranslations('common')

  const { validatePhoneNumberAsync } = useValidatePhoneNumber()
  const { requestOcrAsync, cancel, isScanning } = usePassportOCR()

  const submitPassportForm = async ({
    contactNumber,
    passportImageUrl,
  }: TPassportVerificationForm) => {
    const initialContactNumber = defaultValues?.contactNumber

    if (initialContactNumber !== contactNumber) {
      const isValidPhoneNumber = await validatePhoneNumberAsync(contactNumber)
      if (!isValidPhoneNumber) return
    }

    await requestOcrAsync(passportImageUrl, {
      onSuccess: ({ ocrToken }) => {
        onSuccessPassport({ contactNumber, passportImageUrl, ocrToken })
      },
      onMaxFail: () => {
        onMaxFail({ contactNumber, passportImageUrl, ocrToken: '' })
      },
    })
  }

  const stepLabel = step === 'passport' ? '1/2' : '2/2'

  useEffect(() => {
    return () => {
      cancel()
    }
  }, [])

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className="h-[690px] gap-0 p-0">
        {step === 'passport' ? (
          <PassportContent
            isScanning={isScanning}
            defaultValues={defaultValues}
            onClose={onClose}
            onSubmit={submitPassportForm}
            submitText={t('button.next')}
            stepLabel={stepLabel}
          />
        ) : (
          <EmailContent
            email={email}
            onSuccess={onSuccess}
            onClose={onClose}
            stepLabel={stepLabel}
          />
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}
